import styled from 'styled-components';

import {
  BaseTypography,
  H1Style,
  H2Style,
  H3Style,
  H4Style,
  H5Style,
  H6Style,
  ParagraphNormalStyle,
  ParagraphSmallStyle,
  ParagraphMiniStyle,
  TooltipUnderlineStyle,
  type BaseTypeInterface,
  type ParagraphInterface,
} from 'DesignLibrary/atoms/typography';

/**
 * RichTextStyleWrapper provides consistent styling for Contentful rich text
 * across the entire application. It applies design system typography styles
 * to rich text elements (headings, paragraphs, lists, links).
 *
 * Use this wrapper around any rich text content from Contentful to ensure
 * consistent spacing, sizing, and styling.
 */

export interface RichTextStyleWrapperProps {
  p?: ParagraphInterface;
  h1?: BaseTypeInterface;
  h2?: BaseTypeInterface;
  h3?: BaseTypeInterface;
  h4?: BaseTypeInterface;
  h5?: BaseTypeInterface;
  h6?: BaseTypeInterface;
}

export const RichTextStyleWrapper = styled.div<RichTextStyleWrapperProps>`
  /* Apply design system typography styles to rich text elements */
  ${BaseTypography('--text-black', 300, 1.5)}

  h1 {
    ${({ h1 }) => {
      const weight = h1?.weight ?? 'bold';
      const color = h1?.color ?? '--text-black';
      const lineHeight = h1?.lineHeight;
      const w = weight === 'normal' ? 400 : 500;
      return H1Style(color, w, lineHeight);
    }}

    ${({ h1 }) => h1?.underline && TooltipUnderlineStyle}
    margin-block: var(--sizing-md) var(--sizing-sm);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h2 {
    ${({ h2 }) => {
      const weight = h2?.weight ?? 'bold';
      const color = h2?.color ?? '--text-black';
      const lineHeight = h2?.lineHeight;
      const w = weight === 'normal' ? 400 : 500;
      return H2Style(color, w, lineHeight);
    }}

    ${({ h2 }) => h2?.underline && TooltipUnderlineStyle}
    margin-block: var(--sizing-md) var(--sizing-sm);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h3 {
    ${({ h3 }) => {
      const weight = h3?.weight ?? 'bold';
      const color = h3?.color ?? '--text-black';
      const lineHeight = h3?.lineHeight;
      const w = weight === 'normal' ? 300 : 500;
      return H3Style(color, w, lineHeight);
    }}

    ${({ h3 }) => h3?.underline && TooltipUnderlineStyle}
    margin-block: var(--sizing-sm) var(--sizing-xs);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h4 {
    ${({ h4 }) => {
      const weight = h4?.weight ?? 'bold';
      const color = h4?.color ?? '--text-black';
      const lineHeight = h4?.lineHeight;
      const w = weight === 'normal' ? 300 : 500;
      return H4Style(color, w, lineHeight);
    }}

    ${({ h4 }) => h4?.underline && TooltipUnderlineStyle}
    margin-block: var(--sizing-sm) var(--sizing-xs);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h5 {
    ${({ h5 }) => {
      const weight = h5?.weight ?? 'bold';
      const color = h5?.color ?? '--text-black';
      const lineHeight = h5?.lineHeight;
      const w = weight === 'normal' ? 300 : 500;
      return H5Style(color, w, lineHeight);
    }}

    ${({ h5 }) => h5?.underline && TooltipUnderlineStyle}
    margin-block: var(--sizing-sm) var(--sizing-xs);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h6 {
    ${({ h6 }) => {
      const color = h6?.color ?? '--text-black';
      return H6Style(color);
    }}

    ${({ h6 }) => h6?.underline && TooltipUnderlineStyle}
    margin-block: var(--sizing-sm) var(--sizing-xs);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  p {
    ${(props) => {
      const size = props.p?.size ?? 'normal';
      const weight = props.p?.weight ?? 'normal';
      const color = props.p?.color ?? '--text-gray';
      const lineHeight = props.p?.lineHeight;
      const w = weight === 'normal' ? 300 : 700;

      switch (size) {
        case 'small':
          return ParagraphSmallStyle(color, w, lineHeight);
        case 'mini':
          return ParagraphMiniStyle(color, w, lineHeight);
        default:
          return ParagraphNormalStyle(color, w, lineHeight);
      }
    }}

    ${({ p }) => p?.underline && TooltipUnderlineStyle}
    margin: var(--sizing-sm) 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    b {
      font-weight: 700;
      color: ${({ p }) => (p?.color === '--text-gray' ? '--text-black' : 'inherit')};
    }
  }

  /* Lists */
  ul,
  ol {
    margin: var(--sizing-sm) 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    ul {
      margin-top: var(--sizing-sm);
    }
  }

  ol {
    list-style-type: decimal;
  }

  li {
    ${(props) => {
      const size = props.p?.size ?? 'normal';
      const weight = props.p?.weight ?? 'normal';
      const color = props.p?.color ?? '--text-gray';
      const lineHeight = props.p?.lineHeight;
      const w = weight === 'normal' ? 300 : 700;

      switch (size) {
        case 'small':
          return ParagraphSmallStyle(color, w, lineHeight);
        case 'mini':
          return ParagraphMiniStyle(color, w, lineHeight);
        default:
          return ParagraphNormalStyle(color, w, lineHeight);
      }
    }}
    display: list-item;
    margin: var(--sizing-xs) 0;
    margin-bottom: 0.5rem !important;

    /* Nested lists */
    ul,
    ol {
      margin: var(--sizing-xs) 0;
    }
  }

  /* Links */
  a {
    color: var(--primary-blue);
    text-decoration: underline;

    &:hover {
      color: var(--primary-blue-dark);
    }
  }
`;
